/**
 * Team domain logic — pure, framework-free helpers для экрана «Команда»
 * (AK-341). Держим здесь всю бизнес-логику, чтобы покрыть vitest'ом без
 * рендера: маппинг статусов, RU-метки ролей, валидация role↔scope.
 *
 * Источники правды (backend):
 *   - INVITABLE_ROLES / ROLE_LABELS_RU — backend/src/ikolhoz/auth/service.py
 *   - _role_scope_ok — backend/src/ikolhoz/api/v1/users.py (invite_user), которое
 *     повторяет CHECK-constraint ck_tm_role_scope на auth.tenant_members.
 *     Клиентская валидация тут — превентивная (UX), финальная — на бэке.
 */

export type ScopeKind = 'company' | 'cluster' | 'farm';

/**
 * RU-метки ролей — 1:1 с ROLE_LABELS_RU бэка. ЕДИНСТВЕННАЯ карта меток во
 * фронте: профиль, выбор кабинета, заведение компании читают её через
 * `roleLabel` (ADR-081 §4 — «одна карта вместо трёх»).
 *
 * ADR-081 §3: «C-level» — англицизм, агроному непонятен → «Директор
 * кластера/хозяйства»; «Главный агроном» без уточнения рядом с «Главный
 * агроном хозяйства» — две одинаковые на вид роли → «…компании/кластера».
 */
export const ROLE_LABELS_RU: Record<string, string> = {
  company_admin: 'Админ компании',
  top_level: 'Управляющая компания',
  board: 'Наблюдатель (совет/семья)',
  clevel: 'Директор кластера/хозяйства',
  clevel_agronom: 'Главный агроном компании/кластера',
  farm_admin: 'Админ хозяйства',
  farm_chief_agronomist: 'Главный агроном хозяйства',
  agronom: 'Агроном',
  monitoring_lady: 'Офис-мониторинг',
  viewer: 'Только чтение',
  // Не выдаются из «Команды», но приходят в /me (профиль, выбор кабинета).
  lawyer: 'Юрист',
  consultant: 'Консультант',
};

/**
 * Роли, которые можно назначить при приглашении/смене — 1:1 с INVITABLE_ROLES
 * бэка (ADR-081 этап B, AK-886: + роли хозяйства). Порядок = порядок в
 * селекторе: компания → кластер → хозяйство → исполнители. Кому какие из них
 * доступны — `can_invite_codes` из `GET /users` (см. `grantableRoles`).
 */
export const INVITABLE_ROLES: readonly string[] = [
  'company_admin',
  'top_level',
  'board',
  'clevel',
  'clevel_agronom',
  'farm_admin',
  'farm_chief_agronomist',
  'agronom',
  'monitoring_lady',
  'viewer',
];

/**
 * Одна строка «что видит и может делать эта роль» — под селектом роли, чтобы
 * админ не пригласил Наблюдателя вместо Агронома по ошибке (см. wireframe).
 */
export const ROLE_HINTS_RU: Record<string, string> = {
  company_admin:
    'Полный доступ: управление командой, все хозяйства, настройки компании.',
  top_level: 'Видит всю компанию и все кластеры. Управление на уровне холдинга.',
  board: 'Только просмотр по всей компании — для совета директоров / собственников.',
  clevel: 'Руководит своим кластером или хозяйством: операции, поля, отчёты своей зоны.',
  clevel_agronom: 'Главный агроном: агрономия по компании или кластеру, техкарты и осмотры.',
  farm_admin:
    'Ведёт своё хозяйство: поля, посевы, технику, план сезона и людей хозяйства. Не меняет настройки компании.',
  farm_chief_agronomist:
    'Ведёт агрономию хозяйства: посевы, операции, осмотры, техкарты, план сезона, закрепляет поля за агрономами. Людей, технику и структуру не меняет.',
  agronom:
    'Ведёт поля, NDVI, операции и осмотры своей зоны. Не управляет командой и настройками.',
  monitoring_lady: 'Офис-мониторинг: заполнение и контроль данных, широкий просмотр.',
  viewer: 'Только чтение — видит поля, карты и осмотры в своей зоне, ничего не меняет.',
};

/**
 * Допустимые scope для каждой роли — зеркало backend `_role_scope_ok`
 * (users.py). Если роли нет в мапе — ограничений нет (любой scope).
 */
export const ROLE_SCOPE_MATRIX: Record<string, readonly ScopeKind[]> = {
  company_admin: ['company'],
  top_level: ['company'],
  board: ['company'],
  clevel: ['cluster', 'farm'],
  clevel_agronom: ['company', 'cluster'],
  // ADR-081 D1: членство роли хозяйства — только строка хозяйства.
  farm_admin: ['farm'],
  farm_chief_agronomist: ['farm'],
  agronom: ['cluster', 'farm'],
  monitoring_lady: ['company', 'cluster', 'farm'],
  viewer: ['company', 'cluster', 'farm'],
};

export const SCOPE_LABELS_RU: Record<ScopeKind, string> = {
  company: 'Вся компания',
  cluster: 'Кластер',
  farm: 'Хозяйство',
};

export function roleLabel(role: string): string {
  return ROLE_LABELS_RU[role] ?? role;
}

/** Разрешён ли данный scope для роли. Роль вне матрицы → любой scope ок. */
export function isScopeAllowedForRole(role: string, scope: ScopeKind): boolean {
  const allowed = ROLE_SCOPE_MATRIX[role];
  if (!allowed) return true;
  return allowed.includes(scope);
}

/** Список допустимых scope'ов для роли (для дизейбла опций в UI). */
export function allowedScopesForRole(role: string): readonly ScopeKind[] {
  return ROLE_SCOPE_MATRIX[role] ?? (['company', 'cluster', 'farm'] as const);
}

// ─── Что управляющий вправе выдать (AK-886, ADR-081 D3 / §4) ────────────────

/**
 * Область управляющего из `GET /users` (`manage_area`). `company_wide` — вся
 * компания, списки пустые; иначе — его кластеры и хозяйства (хозяйства его
 * кластеров бэк уже включил в `farm_ids`).
 */
export interface ManageArea {
  company_wide: boolean;
  cluster_ids: string[];
  farm_ids: string[];
}

/**
 * Роли для селектора: `can_invite_codes` из ответа, в порядке селектора.
 * Поля нет (бэк до AK-886) — прежний полный список: бэк всё равно проверит.
 * Код, которого фронт не знает (deprecated, `lawyer`), в селектор не попадает.
 */
export function grantableRoles(codes?: readonly string[] | null): string[] {
  if (codes == null) return [...INVITABLE_ROLES];
  const allowed = new Set(codes);
  return INVITABLE_ROLES.filter((r) => allowed.has(r));
}

/**
 * Зоны, которые показать в выборе: совместимые с ролью (ROLE_SCOPE_MATRIX) и
 * лежащие в области управляющего. Админ хозяйства не видит «Вся компания» и
 * «Кластер» (ADR-081 §4); гл. агроном кластера — «Вся компания».
 */
export function scopesForManager(role: string, area?: ManageArea | null): ScopeKind[] {
  const inArea = areaScopeKinds(area);
  return allowedScopesForRole(role).filter((s) => inArea.includes(s));
}

/** Виды зон в области управляющего — без учёта роли (что вообще показывать). */
export function areaScopeKinds(area?: ManageArea | null): ScopeKind[] {
  if (!area || area.company_wide) return ['company', 'cluster', 'farm'];
  const kinds: ScopeKind[] = [];
  if (area.cluster_ids.length > 0) kinds.push('cluster');
  if (area.farm_ids.length > 0) kinds.push('farm');
  return kinds;
}

/** Кластеры, в которые управляющий может выдать роль. */
export function areaClusters<T extends { id: string }>(clusters: T[], area?: ManageArea | null): T[] {
  if (!area || area.company_wide) return clusters;
  const ids = new Set(area.cluster_ids);
  return clusters.filter((c) => ids.has(c.id));
}

/** Хозяйства, в которые управляющий может выдать роль. */
export function areaFarms<T extends { id: string }>(farms: T[], area?: ManageArea | null): T[] {
  if (!area || area.company_wide) return farms;
  const ids = new Set(area.farm_ids);
  return farms.filter((f) => ids.has(f.id));
}

/**
 * Что подставить в выбор хозяйства/кластера: текущее из переключателя (если оно
 * среди доступных), иначе единственный вариант. Несколько и ничего не выбрано —
 * пусто, человек выбирает сам.
 */
export function defaultScopeId(options: { id: string }[], preferredId?: string | null): string | null {
  if (preferredId && options.some((o) => o.id === preferredId)) return preferredId;
  return options.length === 1 ? options[0].id : null;
}

// ─── Селектор роли: группы ─────────────────────────────────────────────────

export const SYS_ROLE_PREFIX = 'sys:';
export const CUSTOM_ROLE_PREFIX = 'custom:';
export const SYSTEM_ROLES_GROUP_LABEL = 'Системные роли';
export const CUSTOM_ROLES_GROUP_LABEL = 'Роли компании';
/** Хвост имени кастомной роли, совпавшего с системной меткой в том же селекторе. */
const CUSTOM_CLASH_SUFFIX = ' (роль компании)';

export interface RoleSelectGroup {
  label: string;
  items: { value: string; label: string; disabled?: boolean }[];
}

/**
 * Группы селектора роли (ADR-081 §4): «Системные роли» — по `can_invite_codes`,
 * «Роли компании» — кастомные, только тому, кто может их назначать (AK-914:
 * `company_admin`). Две строки с одинаковым текстом в одном селекторе
 * недопустимы: кастомная роль, названная как системная, получает хвост.
 */
export function roleSelectGroups(
  roles: readonly string[],
  custom: readonly { id: string; name: string }[],
  allowCustom: boolean,
  /**
   * Текущая системная роль участника. Если выдать её управляющий не может, она
   * всё равно видна в селекторе (недоступной строкой) — иначе поле роли пустое
   * и непонятно, что у человека сейчас.
   */
  currentRole?: string | null,
): RoleSelectGroup[] {
  const allowed = new Set(grantableRoles(roles));
  const shown = currentRole && !allowed.has(currentRole) ? [...allowed, currentRole] : [...allowed];
  const system = grantableRoles(shown).map((code) => ({
    value: `${SYS_ROLE_PREFIX}${code}`,
    label: roleLabel(code),
    ...(allowed.has(code) ? {} : { disabled: true }),
  }));
  const groups: RoleSelectGroup[] = [{ label: SYSTEM_ROLES_GROUP_LABEL, items: system }];
  if (allowCustom && custom.length > 0) {
    const taken = new Set(system.map((i) => i.label));
    groups.push({
      label: CUSTOM_ROLES_GROUP_LABEL,
      items: custom.map((r) => {
        const label = taken.has(r.name) ? `${r.name}${CUSTOM_CLASH_SUFFIX}` : r.name;
        taken.add(label);
        return { value: `${CUSTOM_ROLE_PREFIX}${r.id}`, label };
      }),
    });
  }
  return groups;
}

/**
 * Валидация формы приглашения/смены доступа на клиенте (превентивная).
 * Возвращает список ошибок (пустой = ок). Финальную проверку делает бэк.
 */
export interface TeamAccessDraft {
  role: string;
  scope: ScopeKind;
  scopeId?: string | null;
  email?: string;
}

export interface TeamValidationResult {
  ok: boolean;
  /** Ошибка на поле email, если есть. */
  emailError?: string;
  /** Ошибка на поле роли/scope, если есть. */
  scopeError?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** @param withEmail — валидировать ли email (invite=true, patch-role=false). */
export function validateAccessDraft(
  draft: TeamAccessDraft,
  withEmail = false,
): TeamValidationResult {
  const result: TeamValidationResult = { ok: true };

  if (withEmail) {
    const email = (draft.email ?? '').trim();
    if (!email) {
      result.ok = false;
      result.emailError = 'Укажите email';
    } else if (!EMAIL_RE.test(email)) {
      result.ok = false;
      result.emailError = 'Некорректный email';
    }
  }

  if (!INVITABLE_ROLES.includes(draft.role)) {
    result.ok = false;
    result.scopeError = 'Выберите роль';
    return result;
  }

  if (!isScopeAllowedForRole(draft.role, draft.scope)) {
    result.ok = false;
    const allowed = allowedScopesForRole(draft.role)
      .map((s) => SCOPE_LABELS_RU[s])
      .join(', ');
    result.scopeError = `Роль «${roleLabel(draft.role)}» не сочетается с этой зоной. Допустимо: ${allowed}.`;
    return result;
  }

  if (draft.scope !== 'company' && !draft.scopeId) {
    result.ok = false;
    result.scopeError =
      draft.scope === 'cluster' ? 'Выберите кластер' : 'Выберите хозяйство';
  }

  return result;
}

// ─── Статусы участника ──────────────────────────────────────────────────────

export type MemberStatus = 'active' | 'blocked' | 'archived';

export interface MemberLike {
  is_active: boolean;
  /** AK-365: расширенный статус с бэка. Fallback на is_active для старых ответов. */
  status?: MemberStatus | string | null;
  accepted_at?: string | null;
}

/**
 * Статус участника (AK-365). Читаем поле `status` с бэка (active / blocked /
 * archived); если его нет (старый ответ) — деривим из is_active.
 */
export function deriveMemberStatus(m: MemberLike): MemberStatus {
  if (m.status === 'active' || m.status === 'blocked' || m.status === 'archived') {
    return m.status;
  }
  return m.is_active ? 'active' : 'blocked';
}

export interface StatusMeta {
  label: string;
  /** Вариант Badge из design system. */
  badgeVariant: 'primary' | 'accent-brand' | 'secondary' | 'outline';
}

export const MEMBER_STATUS_META: Record<MemberStatus, StatusMeta> = {
  active: { label: 'Активен', badgeVariant: 'primary' },
  blocked: { label: 'Заблокирован', badgeVariant: 'accent-brand' },
  archived: { label: 'В архиве', badgeVariant: 'secondary' },
};

export const INVITATION_STATUS_META = {
  invited: { label: 'Приглашён', badgeVariant: 'outline' as const },
  expired: { label: 'Истекло', badgeVariant: 'secondary' as const },
};

/** Инициалы для аватара из имени/почты. */
export function initialsOf(name?: string | null, email?: string | null): string {
  const src = (name ?? '').trim();
  if (src) {
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return src.slice(0, 2).toUpperCase();
  }
  const e = (email ?? '').trim();
  return e ? e.slice(0, 2).toUpperCase() : '?';
}

/**
 * Осталось до истечения приглашения. Возвращает {expired, label}.
 */
export function invitationTimeLeft(
  expiresAt: string,
  now: Date = new Date(),
): { expired: boolean; label: string } {
  const exp = new Date(expiresAt).getTime();
  const ms = exp - now.getTime();
  if (ms <= 0) return { expired: true, label: 'истекло' };
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  const plural = days % 10 === 1 && days % 100 !== 11 ? 'день' : days >= 2 && days <= 4 ? 'дня' : 'дней';
  return { expired: false, label: `истекает через ${days} ${plural}` };
}

/**
 * Флаги «что реально умеет бэк». AK-365 оживил полный набор статусов:
 * block (active→blocked), unblock (blocked→active), archive
 * (active|blocked→archived, насовсем — реактивации из архива нет).
 *   POST /users/members/{id}/block | unblock | archive
 */
export const teamCapabilities = {
  invite: true,
  revokeInvitation: true,
  resendInvitation: false, // resend-эндпоинта пока нет
  changeRole: true,
  block: true, // POST /users/members/{id}/block
  unblock: true, // POST /users/members/{id}/unblock (blocked→active)
  archive: true, // POST /users/members/{id}/archive (насовсем)
  reactivate: false, // из архива реактивации нет (archived = насовсем)
} as const;

export const CAPABILITY_SOON_HINT = 'Скоро — бэкенд дорабатывается';

/** Можно ли выполнять деструктивные действия над этой строкой (не над собой). */
export function canActOnMember(memberUserId: string, currentUserId?: string | null): boolean {
  if (!currentUserId) return true;
  return memberUserId !== currentUserId;
}
